import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import DictionaryPanel from '../../components/DictionaryPanel';
import './Flashcards.css';

function Flashcards() {
    const [words, setWords] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [lastAnswer, setLastAnswer] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);
    const [dictionaryMobileOpen, setDictionaryMobileOpen] = useState(false);
    
    // حالت ویرایش
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingWord, setEditingWord] = useState(null);
    const [editForm, setEditForm] = useState({ word: '', meaning: '', context: '' });

    useEffect(() => {
        const userData = localStorage.getItem('currentUser');
        if (userData) {
            setCurrentUser(JSON.parse(userData));
        } else {
            window.location.href = '/login';
        }
    }, []);

    useEffect(() => {
        if (currentUser) {
            fetchFlashcards();
            fetchStats();
        }
    }, [currentUser]);

    const fetchFlashcards = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/flashcards/${currentUser._id}`);
            
            // اگر کاربر اشتراک ندارد
            if (response.data.needSubscription) {
                setWords([]);
                setLoading(false);
                return;
            }
            
            setWords(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching flashcards:", error);
            setLoading(false);
        }
    };
    
    // در بخش نمایش خالی
    if (words.length === 0 && !loading) {
        const hasSubscription = currentUser?.subscription?.isActive;
        
        return (
            <>
                <Navbar />
                <div className="flashcards-empty">
                    {!hasSubscription ? (
                        <>
                            <h2>🔒 دسترسی به فلش‌کارت فقط برای کاربران دارای اشتراک</h2>
                            <p>برای استفاده از فلش‌کارت و ذخیره کلمات، لطفاً اشتراک تهیه کنید.</p>
                            <a href="/subscription" className="btn-start-reading">🎁 خرید اشتراک</a>
                        </>
                    ) : (
                        <>
                            <h2>📭 امروز هیچ کلمه ای برای مرور ندارید</h2>
                            <p>در حین مطالعه، روی کلمات کلیک کنید و دکمه ذخیره را بزنید.</p>
                            <a href="/summary-books" className="btn-start-reading">شروع مطالعه</a>
                        </>
                    )}
                </div>
                <Footer />
            </>
        );
    }

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/flashcard-stats/${currentUser._id}`);
            setStats(response.data.data);
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const handleCorrect = async () => {
        const currentWord = words[currentIndex];
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/correct-answer/${currentWord._id}`);
            setLastAnswer({ success: true, message: response.data.message });
            setShowResult(true);
            
            const newWords = words.filter((_, i) => i !== currentIndex);
            setWords(newWords);
            
            if (currentIndex >= newWords.length) {
                setCurrentIndex(Math.max(0, newWords.length - 1));
            }
            
            setTimeout(() => setShowResult(false), 1500);
            fetchStats();
        } catch (error) {
            console.error("Error:", error);
        }
        setIsFlipped(false);
    };

    const handleIncorrect = async () => {
        const currentWord = words[currentIndex];
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/incorrect-answer/${currentWord._id}`);
            setLastAnswer({ success: false, message: response.data.message });
            setShowResult(true);
            
            const currentWordUpdated = { ...currentWord, box: 1 };
            const newWords = [...words.slice(0, currentIndex), ...words.slice(currentIndex + 1), currentWordUpdated];
            setWords(newWords);
            
            setTimeout(() => setShowResult(false), 1500);
            fetchStats();
        } catch (error) {
            console.error("Error:", error);
        }
        setIsFlipped(false);
    };

    // تابع حذف کلمه
    const handleDeleteWord = async (wordId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/saved-word/${wordId}`);
            
            const newWords = words.filter((_, i) => i !== currentIndex);
            setWords(newWords);
            
            if (currentIndex >= newWords.length) {
                setCurrentIndex(Math.max(0, newWords.length - 1));
            }
            
            setShowDeleteConfirm(false);
            setWordToDelete(null);
            fetchStats();
            
            setLastAnswer({ success: true, message: "کلمه با موفقیت حذف شد" });
            setShowResult(true);
            setTimeout(() => setShowResult(false), 1500);
            
        } catch (error) {
            console.error("Error deleting word:", error);
            setLastAnswer({ success: false, message: "خطا در حذف کلمه" });
            setShowResult(true);
            setTimeout(() => setShowResult(false), 2000);
        }
    };

    // تابع ویرایش کلمه (باز کردن مودال)
    const openEditModal = () => {
        const currentWord = words[currentIndex];
        setEditingWord(currentWord);
        setEditForm({
            word: currentWord.word,
            meaning: currentWord.meaning,
            context: currentWord.context || ''
        });
        setShowEditModal(true);
    };

    // تابع ذخیره ویرایش
    const handleSaveEdit = async () => {
        if (!editingWord) return;
        
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/saved-word/${editingWord._id}`, {
                word: editForm.word,
                meaning: editForm.meaning,
                context: editForm.context
            });
            
            // به‌روزرسانی لیست کلمات
            const updatedWords = [...words];
            updatedWords[currentIndex] = {
                ...updatedWords[currentIndex],
                word: editForm.word,
                meaning: editForm.meaning,
                context: editForm.context
            };
            setWords(updatedWords);
            
            setShowEditModal(false);
            setEditingWord(null);
            fetchStats();
            
            setLastAnswer({ success: true, message: "کلمه با موفقیت ویرایش شد" });
            setShowResult(true);
            setTimeout(() => setShowResult(false), 1500);
            
        } catch (error) {
            console.error("Error editing word:", error);
            setLastAnswer({ success: false, message: "خطا در ویرایش کلمه" });
            setShowResult(true);
            setTimeout(() => setShowResult(false), 2000);
        }
    };

    const openDeleteConfirm = () => {
        setWordToDelete(words[currentIndex]);
        setShowDeleteConfirm(true);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setWordToDelete(null);
    };

    const getBoxLabel = (box) => {
        switch(box) {
            case 1: return '📦 جعبه 1 (روزانه)';
            case 2: return '📦 جعبه 2 (هر 2 روز)';
            case 3: return '📦 جعبه 3 (هر 4 روز)';
            case 4: return '📦 جعبه 4 (هر 8 روز)';
            case 5: return '🏆 جعبه 5 (هر 16 روز)';
            default: return '📦 جعبه 1';
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="flashcards-loading">در حال بارگذاری...</div>
                <Footer />
            </>
        );
    }

    if (words.length === 0) {
        // بررسی کنید کاربر بدون اشتراک است و به حد مجاز رسیده
        const hasSubscription = currentUser?.subscription?.isActive;
        const hasAnyWords = stats?.total > 0; // اگر قبلاً کلمه ذخیره کرده
        
        return (
            <>
                <Navbar />
                <div className="flashcards-empty">
                    {!hasSubscription && hasAnyWords ? (
                        <>
                            <h2>🔒 به حداکثر کلمات رایگان رسیده‌اید!</h2>
                            <p>شما قبلاً ۱ کلمه را ذخیره کرده‌اید.</p>
                            <p>کاربران بدون اشتراک فقط می‌توانند ۱ کلمه ذخیره کنند.</p>
                            <p>برای ادامه و دسترسی به فلش‌کارت نامحدود، اشتراک تهیه کنید.</p>
                            <a href="/subscription" className="btn-start-reading">🎁 خرید اشتراک</a>
                        </>
                    ) : !hasSubscription && !hasAnyWords ? (
                        <>
                            <h2>📭 هیچ کلمه ذخیره شده‌ای ندارید</h2>
                            <p>کاربران بدون اشتراک می‌توانند ۱ کلمه را به صورت رایگان ذخیره کنند.</p>
                            <p>در حین مطالعه، روی کلمات کلیک کنید و دکمه ذخیره را بزنید.</p>
                            <a href="/summary-books" className="btn-start-reading">شروع مطالعه</a>
                        </>
                    ) : (
                        <>
                            <h2>📭 هیچ کلمه ذخیره شده‌ای ندارید</h2>
                            <p>در حین مطالعه، روی کلمات کلیک کنید و دکمه ذخیره را بزنید.</p>
                            <a href="/summary-books" className="btn-start-reading">شروع مطالعه</a>
                        </>
                    )}
                </div>
                <Footer />
            </>
        );
    }

    const currentWord = words[currentIndex];

    return (
        <>
            <Navbar />
            <div className="flashcards-container">
                {/* آمار لایتنر */}
                <div className="leitner-stats">
                    <div className="stats-cards">
                        {stats?.boxCounts && Object.entries(stats.boxCounts).map(([box, count]) => (
                            <div key={box} className={`stat-card box-${box} ${currentWord?.box == box ? 'active' : ''}`}>
                                <span className="box-number">ج{box}</span>
                                <span className="box-count">{count}</span>
                            </div>
                        ))}
                    </div>
                    <div className="due-today">
                        📅 امروز: {words.length} کلمه برای مرور
                    </div>
                </div>

                {/* دکمه‌های عملیاتی */}
                <div className="flashcard-header-actions">
                    <button className="edit-card-btn" onClick={openEditModal} title="ویرایش کارت">
                        ✏️ ویرایش
                    </button>
                    <button className="delete-card-btn" onClick={openDeleteConfirm} title="حذف کارت">
                        🗑️ حذف
                    </button>
                </div>

                {/* فلش‌کارت */}
                <div className={`flashcard ${isFlipped ? 'flipped' : ''}`} onClick={() => setIsFlipped(!isFlipped)}>
                    <div className="flashcard-front">
                        <div className="box-badge">{getBoxLabel(currentWord.box)}</div>
                        <h2>{currentWord.word}</h2>
                        <p className="flip-hint">👇 برای دیدن معنی کلیک کنید</p>
                    </div>
                    <div className="flashcard-back">
                        <p className="meaning">{currentWord.meaning}</p>
                        {currentWord.context && (
                            <p className="context">📖 مثال: {currentWord.context}</p>
                        )}
                        {currentWord.bookTitle && (
                            <p className="book-source">📚 منبع: {currentWord.bookTitle}</p>
                        )}
                        <div className="review-info">
                            <span>✅ مرور شده: {currentWord.reviewCount} بار</span>
                            <span>📈 پاسخ صحیح متوالی: {currentWord.correctCount}</span>
                        </div>
                    </div>
                </div>

                {/* دکمه‌ها */}
                <div className="flashcard-actions">
                    <button className="btn-dontknow" onClick={handleIncorrect}>
                        ❌ یادم نیست
                    </button>
                    <button className="btn-know" onClick={handleCorrect}>
                        ✅ بلدم
                    </button>
                </div>

                {/* پیام نتیجه */}
                {showResult && (
                    <div className={`result-message ${lastAnswer?.success ? 'success' : 'error'}`}>
                        {lastAnswer?.message}
                    </div>
                )}

                {/* مودال تأیید حذف */}
                {showDeleteConfirm && wordToDelete && (
                    <div className="delete-confirm-modal">
                        <div className="delete-confirm-content">
                            <h3>🗑️ حذف کلمه</h3>
                            <p>آیا از حذف کلمه "<strong>{wordToDelete.word}</strong>" مطمئن هستید؟</p>
                            <p className="delete-warning">این عمل قابل بازگشت نیست.</p>
                            <div className="delete-confirm-buttons">
                                <button className="confirm-yes" onClick={() => handleDeleteWord(wordToDelete._id)}>
                                    بله، حذف شود
                                </button>
                                <button className="confirm-no" onClick={closeDeleteConfirm}>
                                    انصراف
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* مودال ویرایش */}
                {showEditModal && editingWord && (
                    <div className="edit-modal">
                        <div className="edit-modal-content">
                            <h3>✏️ ویرایش کلمه</h3>
                            
                            <div className="edit-form-group">
                                <label>کلمه (انگلیسی)</label>
                                <input 
                                    type="text" 
                                    className="edit-input"
                                    value={editForm.word}
                                    onChange={(e) => setEditForm({ ...editForm, word: e.target.value })}
                                />
                            </div>
                            
                            <div className="edit-form-group">
                                <label>معنی (فارسی)</label>
                                <textarea 
                                    className="edit-textarea"
                                    value={editForm.meaning}
                                    onChange={(e) => setEditForm({ ...editForm, meaning: e.target.value })}
                                    rows="3"
                                />
                            </div>
                            
                            <div className="edit-form-group">
                                <label>مثال (اختیاری)</label>
                                <textarea 
                                    className="edit-textarea"
                                    value={editForm.context}
                                    onChange={(e) => setEditForm({ ...editForm, context: e.target.value })}
                                    rows="2"
                                    placeholder="جمله‌ای که کلمه در آن استفاده شده..."
                                />
                            </div>
                            
                            <div className="edit-modal-buttons">
                                <button className="edit-save-btn" onClick={handleSaveEdit}>
                                    💾 ذخیره تغییرات
                                </button>
                                <button className="edit-cancel-btn" onClick={() => setShowEditModal(false)}>
                                    انصراف
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <DictionaryPanel
                selectedWord={currentWord?.word || ''}
                apiBase={process.env.REACT_APP_API_URL}
                userId={currentUser?._id}
                bookId={currentWord?._id}
                bookTitle="لایتنر"
                isMobileOpen={dictionaryMobileOpen}
                onToggleMobile={() => setDictionaryMobileOpen(v => !v)}
            />
            <Footer />
        </>
    );
}

export default Flashcards;